/**
 * NotificationCard — Rich notification card with category-specific layouts.
 *
 * Variants:
 *   - Settlement (bet_won/lost/pushed/voided): odds badge, leg count, DNP pills
 *   - Leg (parlay_leg_hit/lost): player headshot, prop line, HIT/MISS badge
 *   - Social (followed_user_*, parlay_shared_*): actor avatar, bold name, odds
 *   - System/Account (everything else): icon + summary + details
 */

import type { Notification } from '@parlay-picker/shared/notification';
import { extractNotificationDisplayData } from '@parlay-picker/shared/notificationDisplay';
import { motion } from 'framer-motion';
import type { ReactElement } from 'react';
import { CardFooter } from './notification/CardFooter';
import { CardHeader } from './notification/CardHeader';
import { COLOR_SCHEME_CLASSES } from './notification/colorScheme';
import { LegBody } from './notification/LegBody';
import { MonitoringBody } from './notification/MonitoringBody';
import { SettlementBody } from './notification/SettlementBody';
import { SocialBody } from './notification/SocialBody';
import { SystemBody } from './notification/SystemBody';

// =============================================================================
// Props
// =============================================================================

interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead: (ids: string[]) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  onNavigateToParlay?: (parlayId: number) => void;
  animationDelay?: number;
  /** Compact mode for dropdown — smaller padding, no nav footer */
  compact?: boolean;
}

// =============================================================================
// Main Component
// =============================================================================

export function NotificationCard({
  notification,
  onMarkAsRead,
  onDismiss,
  onNavigateToParlay,
  animationDelay = 0,
  compact = false,
}: NotificationCardProps): ReactElement {
  const displayData = extractNotificationDisplayData(notification);
  const isUnread = !notification.readAt;
  const colors = COLOR_SCHEME_CLASSES[displayData.colorScheme];

  const handleCardClick = () => {
    if (displayData.hasNavigation && onNavigateToParlay && displayData.parlayId != null) {
      onNavigateToParlay(displayData.parlayId);
    }
  };

  const cardContent = (
    <div
      className={`flex flex-col rounded-lg border transition-all ${
        compact ? 'px-3 py-2' : 'px-4 py-3'
      } ${
        isUnread
          ? 'bg-theme-primary/5 border-theme-primary/20'
          : 'bg-theme-surface border-theme-border/50 hover:bg-theme-surface-hover/50'
      } ${displayData.hasNavigation && onNavigateToParlay ? 'cursor-pointer' : ''}`}
      style={{ borderLeftWidth: 3, borderLeftColor: colors.stripe }}
      onClick={displayData.hasNavigation && onNavigateToParlay ? handleCardClick : undefined}
      role={displayData.hasNavigation && onNavigateToParlay ? 'button' : undefined}
      tabIndex={displayData.hasNavigation && onNavigateToParlay ? 0 : undefined}
    >
      <CardHeader
        notification={notification}
        displayData={displayData}
        compact={compact}
        onMarkAsRead={
          !compact && (displayData.variant === 'leg' || displayData.variant === 'settlement')
            ? onMarkAsRead
            : undefined
        }
        onDismiss={
          !compact && (displayData.variant === 'leg' || displayData.variant === 'settlement')
            ? onDismiss
            : undefined
        }
      />

      {displayData.variant === 'settlement' && (
        <SettlementBody displayData={displayData} notification={notification} />
      )}
      {displayData.variant === 'leg' && (
        <LegBody displayData={displayData} notification={notification} />
      )}
      {displayData.variant === 'social' && (
        <SocialBody displayData={displayData} notification={notification} />
      )}
      {displayData.variant === 'system' && (
        <SystemBody displayData={displayData} notification={notification} />
      )}
      {displayData.variant === 'monitoring' && (
        <MonitoringBody displayData={displayData} notification={notification} />
      )}

      {!compact && displayData.variant !== 'leg' && displayData.variant !== 'settlement' && (
        <CardFooter
          notification={notification}
          displayData={displayData}
          onMarkAsRead={onMarkAsRead}
          onDismiss={onDismiss}
          onNavigateToParlay={onNavigateToParlay}
        />
      )}
    </div>
  );

  // In compact mode (dropdown), skip the motion wrapper
  if (compact) return cardContent;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: animationDelay, duration: 0.2, ease: 'easeOut' }}
    >
      {cardContent}
    </motion.div>
  );
}
